import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, List, ListItemButton, ListItemAvatar, Avatar, Typography, TextField, InputAdornment,
  Alert, CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { chatAPI } from '../../services/api';

/**
 * Lista de chats de proyecto, como la de WhatsApp: el del ultimo mensaje
 * arriba, con su contador de no leidos. La usan la app movil y la pantalla
 * Chats del gestor.
 */

const POLL_MS = 8000;
const AVATAR_COLORS = ['#c62828', '#1565c0', '#2e7d32', '#6a1b9a', '#ef6c00', '#00838f', '#4e342e', '#283593'];

const listTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  }
  const yesterday = new Date();
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Ayer';
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

export const chatInitials = (name) => (name || '?').split(/\s+/).filter(Boolean).slice(0, 2)
  .map((w) => w[0].toUpperCase()).join('');
const initials = chatInitials;

export default function ChatList({ onOpenChat, onUnreadChange, selectedId = null, refreshKey = 0 }) {
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await chatAPI.conversations();
      setChats(res.data);
      setError('');
      if (onUnreadChange) onUnreadChange(res.data.reduce((sum, c) => sum + (c.unread || 0), 0));
    } catch (err) {
      setError(err.response?.data?.detail || 'No se han podido cargar los chats');
    } finally {
      setLoading(false);
    }
  }, [onUnreadChange]);

  useEffect(() => {
    load();
    const timer = setInterval(() => { if (document.visibilityState === 'visible') load(); }, POLL_MS);
    const onSwMessage = (event) => { if (event.data?.type === 'chat-push') load(); };
    navigator.serviceWorker?.addEventListener('message', onSwMessage);
    return () => {
      clearInterval(timer);
      navigator.serviceWorker?.removeEventListener('message', onSwMessage);
    };
  }, [load]);

  // Al abrir o escribir en un chat, quien lo monta sube refreshKey para que el
  // contador de no leidos baje sin esperar al siguiente sondeo
  useEffect(() => { if (refreshKey) load(); }, [refreshKey, load]);

  const q = query.trim().toLowerCase();
  const visible = q
    ? chats.filter((c) => [c.name, c.code, c.client_name].some((v) => (v || '').toLowerCase().includes(q)))
    : chats;

  return (
    <Box>
      <Box sx={{ px: 1, pt: 1 }}>
        <TextField fullWidth size="small" placeholder="Buscar proyecto, codigo o cliente"
          value={query} onChange={(e) => setQuery(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fff', borderRadius: 5 } }} />
      </Box>

      {error && <Alert severity="error" sx={{ m: 1 }}>{error}</Alert>}
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}

      <List sx={{ pt: 0.5 }}>
        {visible.map((chat) => (
          <ListItemButton key={chat.project_id} onClick={() => onOpenChat(chat.project_id)}
            selected={chat.project_id === selectedId}
            sx={{ borderBottom: '1px solid #eee', bgcolor: '#fff', py: 1,
                  '&.Mui-selected': { bgcolor: '#f0f2f5' } }}>
            <ListItemAvatar>
              <Avatar sx={{ bgcolor: AVATAR_COLORS[chat.project_id % AVATAR_COLORS.length], width: 46, height: 46 }}>
                {initials(chat.name)}
              </Avatar>
            </ListItemAvatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography noWrap fontWeight={600} sx={{ flex: 1 }}>
                  {chat.name}
                </Typography>
                <Typography variant="caption" sx={{ color: chat.unread ? '#25d366' : 'text.secondary', flexShrink: 0 }}>
                  {listTime(chat.last_message?.created_at)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary" noWrap sx={{ flex: 1 }}>
                  {chat.last_message
                    ? `${chat.last_message.is_mine ? 'Tu' : chat.last_message.author_name.split(' ')[0]}: ${chat.last_message.preview}`
                    : [chat.code, chat.client_name].filter(Boolean).join(' · ')}
                </Typography>
                {chat.unread > 0 && (
                  <Box sx={{ bgcolor: '#25d366', color: '#fff', borderRadius: 10, minWidth: 20, height: 20,
                             px: 0.75, fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center',
                             justifyContent: 'center', flexShrink: 0 }}>
                    {chat.unread > 99 ? '99+' : chat.unread}
                  </Box>
                )}
              </Box>
            </Box>
          </ListItemButton>
        ))}
      </List>

      {!loading && !visible.length && (
        <Typography align="center" color="text.secondary" sx={{ mt: 4 }}>
          {q ? 'Ningun proyecto coincide con la busqueda' : 'No hay proyectos activos'}
        </Typography>
      )}
    </Box>
  );
}
